import { useEffect, useRef } from 'react';

/**
 * Periodically re-run `callback` while the tab is visible, like a lightweight
 * live feed. Pauses in background tabs and catches up on focus.
 */
export default function useLiveRefresh(callback, intervalMs = 10000, enabled = true) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;

    const run = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        await callbackRef.current?.();
      } catch (err) {
        console.error('Live refresh failed:', err);
      } finally {
        inFlightRef.current = false;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        run();
      }
    };

    const timer = window.setInterval(run, intervalMs);
    window.addEventListener('focus', run);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', run);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs, enabled]);
}
